import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../lib/api';
import { Bell, Plus, Trash2, TestTube, Edit2, CheckCircle2, Loader2, Hash, MessageSquare, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';

const CHANNEL_ICONS = { slack: Hash, teams: MessageSquare, pagerduty: AlertTriangle };
const CHANNEL_COLORS = { slack: 'text-green-400', teams: 'text-blue-400', pagerduty: 'text-emerald-400' };

const Notifications = () => {
  const [channels, setChannels] = useState([]);
  const [types, setTypes] = useState({ channel_types: [], event_types: [] });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testing, setTesting] = useState('');
  const [form, setForm] = useState({ name: '', channel_type: 'slack', webhook_url: '', enabled: true, events: ['incident', 'security'] });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [ch, ty] = await Promise.all([axios.get(`${API}/notifications/channels`), axios.get(`${API}/notifications/types`)]);
      setChannels(ch.data.channels || []);
      setTypes(ty.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const save = async () => {
    try {
      await axios.post(`${API}/notifications/channels`, form);
      toast.success('Channel added');
      setDialogOpen(false);
      setForm({ name: '', channel_type: 'slack', webhook_url: '', enabled: true, events: ['incident', 'security'] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/notifications/channels/${id}`);
    toast.success('Channel removed');
    fetchData();
  };

  const test = async (id) => {
    setTesting(id);
    try {
      const r = await axios.post(`${API}/notifications/channels/${id}/test`);
      r.data.status === 'success' ? toast.success('Test notification sent!') : toast.error(r.data.message);
    } catch (e) { toast.error('Test failed'); } finally { setTesting(''); }
  };

  const toggleEvent = (evt) => {
    setForm(p => ({ ...p, events: p.events.includes(evt) ? p.events.filter(e => e !== evt) : [...p.events, evt] }));
  };

  const placeholder = types.channel_types?.find(t => t.id === form.channel_type)?.webhook_placeholder || '';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Chivo']">Notification Channels</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure Slack, Teams, and PagerDuty alerts</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-channel-btn"><Plus className="w-4 h-4 mr-2" />Add Channel</Button>
      </div>

      {channels.length === 0 ? (
        <Card className="bg-card border-border"><CardContent className="py-12 text-center text-muted-foreground">
          <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" /><p>No notification channels configured</p>
          <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>Add your first channel</Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {channels.map(ch => {
            const Icon = CHANNEL_ICONS[ch.channel_type] || Bell;
            const color = CHANNEL_COLORS[ch.channel_type] || 'text-primary';
            return (
              <Card key={ch.id} className="bg-card border-border" data-testid={`channel-${ch.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-secondary ${color}`}><Icon className="w-5 h-5" /></div>
                      <div>
                        <p className="font-medium">{ch.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{ch.channel_type}</p>
                      </div>
                    </div>
                    <Badge variant={ch.enabled ? "outline" : "secondary"} className={ch.enabled ? "text-green-400 border-green-400/30" : ""}>
                      {ch.enabled ? "Active" : "Disabled"}
                    </Badge>
                  </div>
                  <div className="flex gap-1.5 mt-3 flex-wrap">
                    {(ch.events || []).map(e => <Badge key={e} variant="secondary" className="text-xs">{e}</Badge>)}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button size="sm" variant="outline" onClick={() => test(ch.id)} disabled={testing === ch.id} data-testid={`test-${ch.id}`}>
                      {testing === ch.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <TestTube className="w-3.5 h-3.5 mr-1" />}Test
                    </Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(ch.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle>Add Notification Channel</DialogTitle><DialogDescription>Connect Slack, Teams, or PagerDuty to receive alerts.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Channel Name</Label><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g. #ops-alerts" className="bg-secondary border-border mt-1" /></div>
            <div><Label>Type</Label>
              <Select value={form.channel_type} onValueChange={v => setForm(p => ({...p, channel_type: v}))}>
                <SelectTrigger className="bg-secondary border-border mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {types.channel_types?.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Webhook URL</Label><Input value={form.webhook_url} onChange={e => setForm(p => ({...p, webhook_url: e.target.value}))} placeholder={placeholder} className="bg-secondary border-border mt-1" /></div>
            <div><Label className="mb-2 block">Events to notify</Label>
              <div className="flex gap-2 flex-wrap">
                {types.event_types?.map(evt => (
                  <Badge key={evt.id} variant={form.events.includes(evt.id) ? "default" : "outline"}
                    className="cursor-pointer px-3 py-1.5" onClick={() => toggleEvent(evt.id)}>{evt.label}</Badge>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.name || !form.webhook_url}><CheckCircle2 className="w-4 h-4 mr-2" />Add Channel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Notifications;
